// (reclaim-20261028) Reclaim rent — close the active wallet's EMPTY token
// accounts (Token + Token-2022, raw balance 0) and get their rent back.
// Signs with whatever wallet is active (local key: no popup; Phantom: one
// popup per batch). Read path is the Helius proxy RPC, same as jupiter.js.
// Skipped, never closed: any non-zero balance (dust included), frozen accounts,
// accounts whose close authority isn't the owner, Token-2022 accounts with
// withheld transfer fees or confidential-transfer state, and the wSOL account
// while a trade looks in flight (wallet had a tx in the last 60 s).
import { Connection, PublicKey, ComputeBudgetProgram, TransactionMessage, VersionedTransaction } from '@solana/web3.js'
import { createCloseAccountInstruction, TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token'

const RPC_URL = import.meta.env.VITE_RPC_URL || 'https://rpc.cheddarterminal.app/rpc'
const connection = new Connection(RPC_URL, { commitment: 'confirmed', wsEndpoint: undefined })
const WSOL = 'So11111111111111111111111111111111111111112'
const BATCH = 20
const CU_PRICE = 50_000          // microlamports — same modest fee as the Meteora path
const CONFIRM_MAX_MS = 90_000
const TRADE_QUIET_MS = 60_000
const sleep = (ms) => new Promise(r => setTimeout(r, ms))
const toPk = (k) => (typeof k === 'string' ? new PublicKey(k) : k)

// Why an account can't be closed, or '' if it can. `info` = parsed token account info.
function skipReason(info, ownerStr) {
  if (!info) return 'gone'
  if (String(info.tokenAmount?.amount ?? '1') !== '0') return 'balance'
  if (info.owner !== ownerStr) return 'owner'
  if (info.state === 'frozen') return 'frozen'
  if (info.closeAuthority && info.closeAuthority !== ownerStr) return 'closeAuthority'
  for (const ext of info.extensions || []) {
    if (ext.extension === 'transferFeeAmount' && Number(ext.state?.withheldAmount || 0) > 0) return 'withheldFees'
    if (ext.extension === 'confidentialTransferAccount') return 'confidential'
  }
  return ''
}

// True when the wallet sent/received a tx in the last minute (or the newest
// one hasn't confirmed yet) — then the wSOL account is left alone.
async function tradeLooksInFlight(owner) {
  try {
    const [s] = await connection.getSignaturesForAddress(owner, { limit: 1 }, 'confirmed')
    if (!s) return false
    if (!s.blockTime) return true
    return Date.now() - s.blockTime * 1000 < TRADE_QUIET_MS
  } catch { return true }   // can't tell → play safe
}

/** Read-only. Returns { accounts:[{pubkey, mint, programId, lamports}], lamports, skipped:{reason:n} }. */
export async function findEmpty(ownerIn) {
  const owner = toPk(ownerIn)
  const ownerStr = owner.toBase58()
  const [spl, t22] = await Promise.all([
    connection.getParsedTokenAccountsByOwner(owner, { programId: TOKEN_PROGRAM_ID }),
    connection.getParsedTokenAccountsByOwner(owner, { programId: TOKEN_2022_PROGRAM_ID }),
  ])
  const all = [...spl.value.map(a => [a, TOKEN_PROGRAM_ID]), ...t22.value.map(a => [a, TOKEN_2022_PROGRAM_ID])]
  const skipped = {}
  const accounts = []
  let wsolBusy = null
  for (const [a, programId] of all) {
    const info = a.account.data?.parsed?.info
    let why = skipReason(info, ownerStr)
    if (!why && info.mint === WSOL) {
      if (wsolBusy === null) wsolBusy = await tradeLooksInFlight(owner)
      if (wsolBusy) why = 'wsolBusy'
    }
    if (why) { if (why !== 'balance') skipped[why] = (skipped[why] || 0) + 1; continue }
    accounts.push({ pubkey: a.pubkey.toBase58(), mint: info.mint, programId: programId.toBase58(), lamports: a.account.lamports || 0 })
  }
  return { accounts, lamports: accounts.reduce((s, x) => s + x.lamports, 0), skipped }
}

// Re-read a batch right before sending; keep only what's still closable.
async function recheck(batch, owner) {
  const ownerStr = owner.toBase58()
  const res = await connection.getMultipleParsedAccounts(batch.map(a => new PublicKey(a.pubkey)), { commitment: 'confirmed' })
  let wsolBusy = null
  const out = []
  for (let i = 0; i < batch.length; i++) {
    const acc = res.value[i]
    const info = acc?.data?.parsed?.info
    if (!acc || skipReason(info, ownerStr)) continue
    if (info.mint === WSOL) {
      if (wsolBusy === null) wsolBusy = await tradeLooksInFlight(owner)
      if (wsolBusy) continue
    }
    out.push({ ...batch[i], lamports: acc.lamports || 0 })
  }
  return out
}

async function waitFor(signature, raw, lastValidBlockHeight) {
  const t0 = Date.now()
  let lastSend = t0
  while (Date.now() - t0 < CONFIRM_MAX_MS) {
    await sleep(1500)
    try {
      const { value: [st] } = await connection.getSignatureStatuses([signature])
      if (st?.err) return { state: 'failed', err: st.err }
      if (st && (st.confirmationStatus === 'confirmed' || st.confirmationStatus === 'finalized')) return { state: 'confirmed' }
      if (!st && Date.now() - lastSend > 3000) {
        lastSend = Date.now()
        const h = await connection.getBlockHeight('confirmed')
        if (h > lastValidBlockHeight) {
          const { value: [st2] } = await connection.getSignatureStatuses([signature], { searchTransactionHistory: true })
          if (st2?.err) return { state: 'failed', err: st2.err }
          return { state: st2 ? 'confirmed' : 'expired' }
        }
        connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 }).catch(() => {})
      }
    } catch { /* RPC hiccup — keep polling */ }
  }
  return { state: 'pending' }
}

/** Close `accounts` (from findEmpty) in batches of 20. onProgress({i, n, state, signature}).
 *  Returns { closed, lamports, results:[{signature, state, count, error}] }. Stops at the first failed batch. */
export async function closeEmpty(accounts, ownerIn, signTransaction, onProgress = () => {}) {
  if (typeof signTransaction !== 'function') throw new Error('Wallet cannot sign')
  const owner = toPk(ownerIn)
  const batches = []
  for (let i = 0; i < accounts.length; i += BATCH) batches.push(accounts.slice(i, i + BATCH))
  const results = []
  let closed = 0, lamports = 0
  for (let i = 0; i < batches.length; i++) {
    const n = batches.length
    onProgress({ i: i + 1, n, state: 'checking' })
    const live = await recheck(batches[i], owner)
    if (!live.length) { results.push({ state: 'skipped', count: 0 }); continue }
    const ixs = live.map(a => createCloseAccountInstruction(new PublicKey(a.pubkey), owner, owner, [], new PublicKey(a.programId)))
    const build = (units, blockhash) => new VersionedTransaction(new TransactionMessage({
      payerKey: owner, recentBlockhash: blockhash,
      instructions: [ComputeBudgetProgram.setComputeUnitLimit({ units }), ComputeBudgetProgram.setComputeUnitPrice({ microLamports: CU_PRICE }), ...ixs],
    }).compileToV0Message())
    try {
      const pre = await connection.getLatestBlockhash('confirmed')
      const sim = await connection.simulateTransaction(build(400_000, pre.blockhash), { sigVerify: false, replaceRecentBlockhash: true, commitment: 'confirmed' })
      if (sim.value.err) throw new Error(`Simulation failed: ${JSON.stringify(sim.value.err)}`)
      const used = sim.value.unitsConsumed || 0
      const units = used > 0 ? Math.min(400_000, Math.ceil(used * 1.2) + 5_000) : 200_000
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
      onProgress({ i: i + 1, n, state: 'signing' })
      const signed = await signTransaction(build(units, blockhash))
      const raw = signed.serialize()
      onProgress({ i: i + 1, n, state: 'sending' })
      const signature = await connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 })
      onProgress({ i: i + 1, n, state: 'confirming', signature })
      const r = await waitFor(signature, raw, lastValidBlockHeight)
      results.push({ signature, state: r.state, count: live.length, error: r.err ? JSON.stringify(r.err) : undefined })
      if (r.state === 'confirmed') { closed += live.length; lamports += live.reduce((s, a) => s + a.lamports, 0) }
      else break
    } catch (e) {
      results.push({ state: 'failed', count: live.length, error: e?.message || String(e) })
      break
    }
  }
  return { closed, lamports, results }
}
